import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import SceneShell from "../components/SceneShell";
import { ChatBubbles, GOLD_GRADIENT } from "../components/ProductLogo";

/**
 * Scene 33 — "Which leads me to…" → SOCIAL WEEK reveal · stage-prop driven.
 *
 *   stage = 0  →  Calm "Which leads me to…" line
 *   stage = 1  →  SOCIAL WEEK reveal with calendar-date glyphs drifting
 *
 * Each stage is its own registered slide for reliable arrow navigation.
 */

// Calendar date strings that drift upward — anchor in the brand pink/gold mood.
const DATE_GLYPHS = [
  "MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN",
  "01", "07", "14", "21", "28",
  "MAY", "JUN", "JUL", "AUG", "SEP", "OCT",
  "WK 21", "WK 22", "WK 23", "WK 24",
];

function FloatingDates() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {DATE_GLYPHS.map((label, i) => {
        const left = (i * 17 + 7) % 92; // pseudo-distributed
        const baseSize = 14 + (i % 5) * 4; // small to mid
        const delay = (i * 0.43) % 6;
        const duration = 14 + (i % 7);
        return (
          <motion.div
            key={`${label}-${i}`}
            className="absolute font-mono"
            style={{
              left: `${left}%`,
              bottom: -40,
              fontSize: baseSize,
              color: "rgba(233,168,32,0.45)",
              letterSpacing: "0.18em",
              fontWeight: 700,
              textShadow: "0 0 18px rgba(233,168,32,0.30)",
            }}
            initial={{ y: 0, opacity: 0 }}
            animate={{ y: -1200, opacity: [0, 0.85, 0.85, 0] }}
            transition={{
              duration,
              repeat: Infinity,
              delay,
              ease: "linear",
              times: [0, 0.18, 0.85, 1],
            }}
          >
            {label}
          </motion.div>
        );
      })}
      {/* Calendar icons sprinkled too */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const left = ((i * 31 + 13) % 100);
        const delay = (i * 0.9 + 1.2) % 7;
        const duration = 16 + i;
        const size = 20 + (i % 3) * 8;
        return (
          <motion.div
            key={`cal-${i}`}
            className="absolute"
            style={{
              left: `${left}%`,
              bottom: -60,
              color: "rgba(233,168,32,0.30)",
            }}
            initial={{ y: 0, opacity: 0, rotate: -5 }}
            animate={{ y: -1200, opacity: [0, 0.55, 0.55, 0], rotate: [-5, 5, -5] }}
            transition={{
              duration,
              repeat: Infinity,
              delay,
              ease: "linear",
              times: [0, 0.18, 0.85, 1],
            }}
          >
            <Calendar style={{ width: size, height: size }} strokeWidth={1.5} />
          </motion.div>
        );
      })}
    </div>
  );
}

function SocialWeekLockup() {
  return (
    <div
      className="select-none pointer-events-none flex flex-col items-center gap-5"
      data-testid="logo-social-week-final"
    >
      {/* Big icon — much larger than text */}
      <ChatBubbles color="#e9a820" accent="#3a2a05" size={28} />

      {/* Wordmark */}
      <div
        className="kn-display leading-[0.85] text-center whitespace-nowrap"
        style={{
          fontSize: "clamp(60px, 9vw, 160px)",
          fontWeight: 900,
          letterSpacing: "-0.04em",
          background: GOLD_GRADIENT,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }}
      >
        <div>SOCIAL</div>
        <div>WEEK</div>
      </div>

      {/* BETA badge */}
      <div
        className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-[10px] sm:text-[11px] font-bold tracking-[0.32em] uppercase"
        style={{
          background: "rgba(233,168,32,0.16)",
          border: "1px solid rgba(233,168,32,0.55)",
          color: "#e9a820",
          boxShadow: "0 0 24px rgba(233,168,32,0.20)",
        }}
        data-testid="social-week-beta"
      >
        <span
          className="w-1.5 h-1.5 rounded-full inline-block"
          style={{
            background: "#e9a820",
            boxShadow: "0 0 8px rgba(233,168,32,0.85)",
          }}
        />
        Beta
      </div>
    </div>
  );
}

export default function Scene33_LeadsMeTo({ stage = 0 }) {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-leads-me-to">
      {/* Ambient gold pulsing */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background:
            stage === 0
              ? [
                  "radial-gradient(ellipse at center, rgba(212,148,10,0.08) 0%, transparent 65%)",
                  "radial-gradient(ellipse at center, rgba(233,168,32,0.16) 0%, transparent 65%)",
                  "radial-gradient(ellipse at center, rgba(212,148,10,0.08) 0%, transparent 65%)",
                ]
              : [
                  "radial-gradient(ellipse at center, rgba(233,168,32,0.30) 0%, transparent 70%)",
                  "radial-gradient(ellipse at center, rgba(233,168,32,0.50) 0%, transparent 70%)",
                  "radial-gradient(ellipse at center, rgba(233,168,32,0.30) 0%, transparent 70%)",
                ],
        }}
        transition={{
          duration: stage === 0 ? 8 : 5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Calendar dates drifting up — only on stage 1 */}
      {stage >= 1 && <FloatingDates />}

      <div className="relative max-w-[1500px] mx-auto w-full text-center">
        {/* "Which leads me to…" — visible only on stage 0 */}
        {stage === 0 ? (
          <motion.h2
            initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.0, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="kn-display text-white leading-[0.95]"
            style={{
              fontSize: "clamp(48px, 6.4vw, 100px)",
              fontWeight: 800,
            }}
            data-testid="leads-me-to-title"
          >
            Which leads me{" "}
            <span
              style={{
                background: GOLD_GRADIENT,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              to…
            </span>
          </motion.h2>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, scale: 0.6, filter: "blur(60px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-center justify-center relative"
              style={{
                filter: "drop-shadow(0 0 80px rgba(233,168,32,0.55))",
              }}
              data-testid="social-week-reveal"
            >
              <SocialWeekLockup />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.4, duration: 1.0 }}
              className="mt-14 text-[11px] tracking-[0.32em] uppercase text-white/55"
            >
              Daily presence · weekly cadence
            </motion.div>
          </>
        )}
      </div>
    </SceneShell>
  );
}
